<template>
  <div class="schedule_show">
    <page-header :title="model.name" :desc="$tc('schedule.name', 1)">
      <li class="breadcrumb-item">
        <router-link :to="{name:'schedules.index'}">{{ $tc('schedule.name', 2) }}</router-link>
      </li>
      <li class="breadcrumb-item">{{ model.name }}</li>
    </page-header>
    <page-content>
      <ntm-block class="short-block" :title="$tc('action.name', 2)">
        <template slot="options">
          <button type="button"
                  @click="openAddGameModal()"
                  data-testid="schedule-create-game"
                  class="btn btn-sm btn-success">{{ $t('game.create') }}
          </button>
          <button type="button"
                  v-if="model.playoff"
                  @click="$router.push({name: 'schedules.playoff.show', params: {uuid: model.uuid}})"
                  class="btn btn-sm btn-success">Playoff
          </button>
          <button v-if="showRegenerateButton"
                  type="button"
                  class="btn btn-sm btn-warning"
                  @click="$refs.regenerateSchedule.open()">{{ $t('schedule.games.regenerate') }}
          </button>
          <button v-if="model.games && Object.keys(model.games).length < 2"
                  type="button"
                  class="btn btn-sm btn-warning"
                  @click="$refs.generateSchedule.open()">{{ $t('schedule.games.generate') }}
          </button>

          <button
            type="button"
            class="btn btn-sm btn-info"
            v-tooltip="$t('tooltip.venueVideos')"
            @click="$router.push({name: 'schedules.venueVideos', params: {uuid: model.uuid}})">
            <i class="fa fa-video-camera"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-info"
            v-tooltip="$t('tooltip.editSchedule')"
            data-testid="schedule-edit"
            @click="$refs.editSchedule.open()"><i class="fa fa-pencil"></i>
          </button>
          <div class="dropdown d-inline-block ml-2">
            <button type="button"
                    class="btn btn-sm btn-info"
                    id="schedule-options-dropdown"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    v-tooltip="$t('tooltip.moreOptions')">
              <i class="fa fa-ellipsis-v"></i>
            </button>
            <div class="dropdown-menu dropdown-menu-right p-0 border-0 font-size-sm"
                 aria-labelledby="schedule-options-dropdown">
              <div class="p-2">
                <button type="button"
                        v-if="!model.playoff"
                        @click="$router.push({name: 'schedules.createPlayoff.show', params: {uuid: model.uuid}})"
                        class="btn btn-sm btn-info ml-0 w-100 mb-2">{{$t('playoff.create')}}
                </button>
                <button type="button"
                        @click="$refs.fantasyCreate.open()"
                        :class="{'btn-info': !model.fantasyLeague?.uuid, 'btn-warning': model.fantasyLeague?.uuid}"
                        class="btn btn-sm ml-0 w-100">{{model.fantasyLeague?.uuid ? $t('fantasy.edit'):  $t('fantasy.create')}}
                </button>
              </div>
            </div>
          </div>

          <!--<button v-if="Object.keys(model.games).length > 0"-->
          <!--type="button"-->
          <!--class="btn btn-sm btn-warning"-->
          <!--@click="regenerate()">{{$t('schedule.games.regenerate')}}-->
          <!--</button>-->
        </template>
      </ntm-block>
      <ntm-block class="short-block" v-if="model.medals.length" :title="$tc('medal.name', 2)" :init-collapse="false">
        <div class="row mb-3">
          <div class="col-sm-12 mb-2" v-for="medal in model.medals" :key="medal.uuid">
            <span :class="getMedalClass(medal.place)"
                  class="tournament-bracket__medal tournament-bracket__medal fa fa-trophy"></span> {{ medal.name }}
          </div>
        </div>
      </ntm-block>
      <ntm-block class="short-block" :title="$t('rankingTable')" :init-collapse="false">
        <template slot="options">
          <button
            type="button"
            class="btn btn-sm btn-info"
            v-tooltip="$t('tooltip.editColors')"
            @click="$modal.show('edit-colors')"><i class="fa fa-paint-brush"></i>
          </button>
        </template>
        <LeagueRankingTable v-if="model.rankingTable" :data="model.rankingTable" :schedule-uuid="model.uuid"></LeagueRankingTable>
      </ntm-block>

      <div class="groups short-block">
        <ntm-block v-for="(group, n) in model.groups"
                   :save-collapse="`schedule-${model.uuid}-group-${group.name}`"
                   :key="group.uuid"
                   :title="model.groups.length > 1 ? $tc('group.name', 1) + ' ' + group.name : $tc('team.name', 2)">
          <template slot="options">
            <!--<input type="text" class="form-control" placeholder="Search"  v-model="search">-->
            <button v-if="model.groups.length === n + 1" type="button" class="btn btn-sm btn-info" @click="addGroup()">{{ $t('group.add') }}</button>
            <button type="button"
                    class="btn btn-sm btn-danger"
                    v-if="n !== 0"
                    v-tooltip="$t('tooltip.deleteGroup')"
                    @click="deleteGroup(group.uuid)"><i class="fa fa-fw fa-trash"></i></button>
          </template>
          <AddTeamForm :key="`add-team-form-${group.uuid}`" :init-open="!group.teams?.length"
                       :schedule-uuid="$route.params.uuid" :group-uuid="group.uuid"
                       @teamAdded="fetchData"></AddTeamForm>
          <div class="row" v-if="group.teams?.length" :id="group.uuid">
            <div class="team col-sm-12"
                 v-for="team in orderedTeams(group.teams)"
                 :id="team.uuid"
                 :key="team.uuid">
              <div class="team-wrapper">
                <span v-if="team.name">{{ team.name }}</span>
                <span v-else><span class="badge badge-info">{{ $t('emptySlot') }}</span></span>
                <div class="team-right-side">
                  <team-players-images-download :schedule="model" :team="team"></team-players-images-download>
                  <router-link
                    :to="{ name: 'schedules.teams.show', params: {uuid: $route.params.uuid, team: team.uuid }}"
                    :class="{'btn-success': team.players_count >= model.minTeamPlayers, 'btn-warning': team.players_count < model.minTeamPlayers}"
                    class="team-right-side-item btn btn-sm mr-2"
                    v-tooltip="$t('tooltip.teamPlayers')">
                    <i class="fa fa-users"></i>
                    <span class="ml-1">{{ team.players_count }}</span>
                  </router-link>
                  <router-link
                    :to="{ name: 'schedules.teams.show', params: {uuid: $route.params.uuid, team: team.uuid }}"
                    class="btn btn-sm btn-primary"
                    v-tooltip="$t('tooltip.editTeam')">
                    <i class="fa fa-pencil"></i>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </ntm-block>
      </div>

      <div class="short-block d-flex justify-content-center mb-3">
        <div class="btn-group">
          <button type="button"
                  class="btn btn-sm"
                  :class="viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="viewMode = 'list'">
            <i class="fa fa-list mr-1"></i>{{ $t('view.list') }}
          </button>
          <button type="button"
                  class="btn btn-sm"
                  :class="viewMode === 'calendar' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="viewMode = 'calendar'">
            <i class="fa fa-calendar mr-1"></i>{{ $t('view.calendar') }}
          </button>
        </div>
      </div>

      <div v-if="viewMode === 'calendar' && model.uuid">
        <le-schedule-calendar-view
          :schedule-uuid="model.uuid"
          @gameClick="onCalendarGameClick"
        ></le-schedule-calendar-view>
      </div>

      <ntm-block v-if="viewMode === 'list' && model.playoffGames && model.playoffGames.length > 0" :title="$tc('playoff.game', 2)">
        <div class="row">
          <div v-for="round in model.playoffGames" :key="round.round" class="game-round col-12">
            <h3>{{ getNameOfRound(round.round) }}</h3>
            <div v-for="(gameRound, gameRoundKey) in groupByGameRound(round)" :key="gameRoundKey">
              <h5 v-if="Object.keys(groupByGameRound(round)).length > 1" class="text-info">{{ $tc('game.name', 1) }} -
                {{ gameRoundKey }}</h5>
              <div class="table-responsive">
                <table class="table table-vcenter table-striped">
                  <thead>
                  <tr>
                    <th>{{ $t('code') }}</th>
                    <th>{{ $t('time') }}</th>
                    <th class="text-right" style="min-width: 300px">{{ $t('home') }}</th>
                    <th style="min-width: 85px" class="text-nowrap"></th>
                    <th class="text-left" style="min-width: 300px">{{ $t('away') }}</th>
                    <th class="text-center" style="width: 100px;">{{ $tc('action.name', 2) }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="game in gameRound" :key="game.uuid" :class="gameStatusColor(game.status)">
                    <td>
                      <div class="btn-group">
                        <button class="btn btn-info btn-sm"
                                v-if="['NOT_PLAYED', 'SCHEDULED', 'FINISHED'].includes(game.status)"
                                v-tooltip="$t('tooltip.rescheduleGame')"
                                @click="scheduleGameModal(game)"><i class="fa fa-clock-o"></i></button>
                        <button class="btn btn-warning btn-sm"
                                v-if="['SCHEDULED'].includes(game.status)"
                                v-tooltip="$t('tooltip.removeFromSchedule')"
                                @click="cancelScheduledGame(game)"><i class="fa fa-times"></i></button>
                      </div>
                      <span class="ml-2 text-nowrap">{{ formattedGameCode(game.code) }}</span>
                    </td>
                    <td><strong style="font-size: 12px;">{{ formatDate(game.time) }}</strong></td>
                    <td class="font-w600 font-size-sm text-right">
                    <span>
                      <router-link v-if="game.teamA" v-html="teamName(game.teamA)"
                                   :to="{ name: 'schedules.teams.show', params: {uuid: $route.params.uuid, team: game.teamA.uuid }}">
                      </router-link>
                      <span v-else class="badge badge-warning" style="font-size: 14px;"
                            v-html="teamName(game.teamA)">
                      </span>
                      <span v-if="game.teamA">
                        <i v-if="game.winner_team_id === game.teamA.id"
                           class="badge badge-success ml-2 p-2 user-select-none">{{ $t('game.w') }}</i>
                      </span>
                      <span v-if="game.teamB">
                          <i v-if="game.winner_team_id === game.teamB.id"
                             class="badge badge-danger ml-2 p-2 user-select-none">{{ $t('game.l') }}</i>
                      </span>
                    </span>

                    </td>
                    <td class="font-w600 font-size-sm text-center"><span>{{
                        game.home_team_score_total
                      }} : {{ game.away_team_score_total }}</span>
                    </td>
                    <td class="font-w600 font-size-sm">
                    <span>
                      <span v-if="game.teamA">
                        <i v-if="game.winner_team_id === game.teamA.id"
                           class="badge badge-danger mr-2 p-2 user-select-none">{{ $t('game.l') }}</i>
                      </span>
                      <span v-if="game.teamB">
                        <i v-if="game.winner_team_id === game.teamB.id"
                           class="badge badge-success mr-2 p-2 user-select-none">{{ $t('game.w') }}</i>
                      </span>
                      <router-link v-if="game.teamB" v-html="teamName(game.teamB)"
                                   :to="{ name: 'schedules.teams.show', params: {uuid: $route.params.uuid, team: game.teamB.uuid }}">
                      </router-link>
                      <span v-else class="badge badge-warning" style="font-size: 14px;"
                            v-html="teamName(game.teamB)">
                      </span>
                    </span>
                    </td>
                    <td class="text-center">
                      <div class="btn-group user-select-none">
                        <template  v-if="game.teamA && game.teamB">
                          <router-link
                            :to="{ name: 'schedules.games.show', params: {uuid: $route.params.uuid, game: game.uuid }}"
                            class="btn btn-sm btn-primary"
                            v-tooltip="$t('tooltip.editGame')">
                            <i class="fa fa-pencil"></i>
                          </router-link>
                          <router-link
                            :to="{ name: 'schedules.games.showGallery', params: {uuid: $route.params.uuid, game: game.uuid }}"
                            class="btn btn-sm btn-primary"
                            v-tooltip="$t('game.gallery')">
                            <i class="fa fa-image"></i>
                          </router-link>
                          <pdf-game-download :game="game" :round="getNameOfRound(round.round)"
                                             :league="model"></pdf-game-download>
                          <docx-game-download :game="game" :round="getNameOfRound(round.round)"
                                              :league="model"></docx-game-download>
                          <xlsx-game-download :game="game" :round="getNameOfRound(round.round)"
                                              :league="model"></xlsx-game-download>
                        </template>

                        <button v-if="['NOT_PLAYED', 'SCHEDULED'].includes(game.status)" @click="swapGameTimeVenueBetweenGames(game)" :class="{'btn-success': swapGame?.uuid === game.uuid, 'btn-info': swapGame?.uuid !== game.uuid}" class="btn btn-sm" v-tooltip="$t('game.swapTimeAndVenue')"><i class="fa fa-arrows-v"></i></button>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ntm-block>
      <ntm-block v-if="viewMode === 'list' && filteredGames && Object.keys(filteredGames).length > 0" :title="$tc('game.name', 2)">
        <template #options>
          <word-league-schedule-download :schedule="model" class="mr-2"></word-league-schedule-download>
          <button type="button"
                  @click="openAddGameModal()"
                  data-testid="schedule-create-game"
                  class="btn btn-sm btn-success">{{ $t('game.create') }}
          </button>
        </template>
        <div class="row">
          <div class="col-12 mb-3">
            <div class="d-flex flex-wrap w-100 mb-3" style="gap: 10px">
              <button @click="toggleLeagueFilterStatus('NOT_PLAYED')" type="button" class="btn"
                      :class="{'btn-primary': leagueGamesFilters.status.includes('NOT_PLAYED')}">
                {{ $tc('game.status.NOT_PLAYED', 2) }}
              </button>
              <button @click="toggleLeagueFilterStatus('SCHEDULED')" type="button" class="btn"
                      :class="{'btn-primary': leagueGamesFilters.status.includes('SCHEDULED')}">
                {{ $tc('game.status.SCHEDULED', 2) }}
              </button>
              <button @click="toggleLeagueFilterStatus('LIVE')" type="button" class="btn"
                      :class="{'btn-primary': leagueGamesFilters.status.includes('LIVE')}">{{
                  $tc('game.status.LIVE', 2)
                }}
              </button>
              <button @click="toggleLeagueFilterStatus('FINISHED')" type="button" class="btn"
                      :class="{'btn-primary': leagueGamesFilters.status.includes('FINISHED')}">
                {{ $tc('game.status.FINISHED', 2) }}
              </button>
              <button @click="toggleLeagueFilterStatus('ARCHIVED')" type="button" class="btn"
                      :class="{'btn-primary': leagueGamesFilters.status.includes('ARCHIVED')}">
                {{ $tc('game.status.ARCHIVED', 2) }}
              </button>

            </div>
            <div class="d-flex w-100 flex-wrap mb-3" style="gap: 10px">
              <div class="custom-control custom-checkbox custom-checkbox-rounded-circle custom-control-lg custom-control-danger mb-1">
                <input type="checkbox" v-model="leagueGamesFilters.isTerminated" id="is-terminated" class="custom-control-input">
                <label class="custom-control-label" for="is-terminated" v-text="$t('formly.fields.isTerminated')"></label>
              </div>
            </div>
            <div class="d-flex w-100 flex-wrap mb-3" style="gap: 10px">
              <multiselect :multiple="true"
                           style="max-width: 300px"
                           track-by="uuid"
                           class="flex-grow-1"
                           :custom-label="(option) => option.name"
                           placeholder="Select team"
                           v-model="leagueGamesFilters.filterByATeams"
                           :options="allTeams">
              </multiselect>
              <multiselect :multiple="true"
                           style="max-width: 300px"
                           track-by="uuid"
                           class="flex-grow-1"
                           :custom-label="(option) => option.name"
                           placeholder="Select team"
                           v-model="leagueGamesFilters.filterByBTeams"
                           :options="allTeams">
              </multiselect>
            </div>
          </div>

          <div v-for="(round, roundName) in filteredGames" :key="roundName" class="game-round col-12">
            <h4 v-if="round.length">{{ $tc('round', 1) }} {{ roundName }}</h4>
            <LeGamesTable @scheduleGameModal="scheduleGameModal" @cancelScheduledGame="cancelScheduledGame" @swapGameTimeVenue="fetchData"
                          :groups="model.groups"
                          v-if="round.length" :games="round" :league="model">
            </LeGamesTable>
          </div>
        </div>
      </ntm-block>

      <ntm-block v-if="viewMode === 'list' && model.friendlyGames && Object.keys(model.friendlyGames).length > 0"
                 :title="$tc('game.friendly', 2)">
        <template slot="options">
          <button class="btn btn-sm btn-danger" @click="deleteAllFriendlyGames()" v-tooltip="$t('tooltip.deleteAllFriendlyGames')">
            <i class="fa fa-fw fa-trash"></i></button>
        </template>
        <div class="row">
          <div v-for="(round, roundName) in model.friendlyGames" :key="roundName" class="game-round col-12">
            <h4>{{ $tc('round', 1) }} {{ roundName }}</h4>
            <div class="table-responsive">
              <LeGamesTable @scheduleGameModal="scheduleGameModal" @cancelScheduledGame="cancelScheduledGame" @swapGameTimeVenue="fetchData"
                            v-if="round.length" :round-name="roundName" :games="round" :league="model">
              </LeGamesTable>
            </div>
          </div>
        </div>
      </ntm-block>

      <ntm-block :title="$tc('action.name', 2)">
        <template slot="options">
          <button class="btn btn-sm btn-warning" @click="deleteAllGames()">
            {{ $t('schedule.delete-league-games') }}
          </button>
          <button type="button"
                  @click="$refs.deleteSchedule.open()"
                  class="btn btn-sm btn-danger">{{ $t('schedule.delete') }}
          </button>
        </template>
      </ntm-block>

      <ntm-modal @opened="deleteScheduleConfirmed = false" @submit="deleteSchedule(model.uuid)" size="sm" :title="$t('schedule.delete')" ref="deleteSchedule">
        <template slot="content">
          <div class="text-center mb-3">
            <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
            <p class="font-size-lg font-w700 text-danger mb-0">{{ $t('schedule.deleteConfirmText') }}</p>
          </div>
          <ul class="mb-3 pl-3">
            <li>{{ $t('schedule.deleteConsequenceTeams') }}</li>
            <li>{{ $t('schedule.deleteConsequenceGames') }}</li>
            <li>{{ $t('schedule.deleteConsequenceGameData') }}</li>
            <li>{{ $t('schedule.deleteConsequenceSetups') }}</li>
          </ul>
          <div class="custom-control custom-checkbox">
            <input type="checkbox" v-model="deleteScheduleConfirmed" id="delete-schedule-confirm" class="custom-control-input">
            <label class="custom-control-label" for="delete-schedule-confirm">{{ $t('schedule.deleteConfirmLabel') }}</label>
          </div>
        </template>
        <template slot="footer">
          <button type="button" class="btn btn-light mr-2" @click="$refs.deleteSchedule.close()">{{ $t('action.cancel') }}</button>
          <button class="btn btn-danger push-15" type="submit" :disabled="!deleteScheduleConfirmed">{{ $t('schedule.delete') }}</button>
        </template>
      </ntm-modal>

      <ntm-modal @submit="updateSchedule()" size="lg" :title="$tc('schedule.edit')" ref="editSchedule">
        <template slot="content">
          <ntm-block :collapsable="false" data-testid="edit-schedule-form">
            <formly-form :form="editScheduleForm" :model="model" :fields="editScheduleFields"></formly-form>
          </ntm-block>
        </template>
        <template slot="footer">
          <button class="btn btn-success push-15" type="submit">{{ $t('save') }}</button>
        </template>
      </ntm-modal>

      <ntm-modal size="sm" :title="$t('fantasy.create')" ref="fantasyCreate">
        <template slot="content">
          <ntm-block :collapsable="false">
            <div class="mb-3">
              <span class="badge badge-pill badge-info text-white mr-1"><i class="fa fa-users"></i> {{model.fantasyLeague?.fantasyRegistrationsCount}}</span>
              <span class="badge badge-pill badge-info text-white"><i class="fa fa-play"></i> {{model.fantasyLeague?.fantasyGameScoresCount}}</span>
            </div>

            <formly-form :form="fantasyCreateForm"
                         :model="fantasyCreateData"
                         :fields="fantasyCreateFields"></formly-form>
          </ntm-block>
        </template>
        <template slot="footer">
          <button class="btn btn-success mr-2" @click="upsertFantasy()">{{model.fantasyLeague?.uuid ?  $t('save') : $t('fantasy.create')}}</button>
          <button v-if="model.fantasyLeague?.uuid" class="btn btn-danger push-15" @click="deleteFantasy()">{{$t('fantasy.delete')}}</button>
        </template>
      </ntm-modal>

      <ntm-modal size="sm" :title="$t('schedule.games.generate')" ref="generateSchedule">
        <template slot="content">
          <ntm-block :collapsable="false">
            <formly-form :form="generateForm"
                         :model="generateData"
                         :fields="generateFields"></formly-form>
          </ntm-block>
        </template>
        <template slot="footer">
          <button class="btn btn-success push-15" @click="generate()">Generate</button>
        </template>
      </ntm-modal>
      <ntm-modal size="md" :title="$t('schedule.games.regenerate')" ref="regenerateSchedule">
        <template slot="content">
          <ntm-block :collapsable="false">
            <div class="alert alert-warning" role="alert" v-html="$t('schedule.games.regenerateInfoHtml')">
            </div>
            <formly-form :form="generateForm"
                         :model="generateData"
                         :fields="generateFields"></formly-form>
          </ntm-block>
        </template>
        <template slot="footer">
          <button class="btn btn-success push-15" @click="regenerate()">{{$t('schedule.games.regenerate')}}</button>
        </template>
      </ntm-modal>
      <ntm-modal size="md" :title="$t('game.create')" ref="addGame">
        <template slot="content">
          <ntm-block :collapsable="false">
            <formly-form data-testid="add-game-form" :form="addGameForm"
                         :model="addGameData"
                         :fields="addGameFields"></formly-form>
          </ntm-block>
        </template>
        <template slot="footer">
          <button class="btn btn-success push-15" data-testid="add-game-save" @click="addGame()">{{ $t('save') }}</button>
        </template>
      </ntm-modal>
    </page-content>
    <modal name="edit-colors" :adaptive="true" height="auto">
      <form @submit.prevent="scheduleGameSubmit">
        <ntm-block :collapsable="false" :title="$tc('rankingColor.name', 2)">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('edit-colors')" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>
          <div class="list-group">
            <div
              v-for="(rc, idx) in model.rankingColors"
              :key="`rc-${idx}-${rc.label}`"
              class="list-group-item d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center" style="gap: 10px">
                <span
                  class="rounded"
                  :style="{ display: 'inline-block', width: '16px', height: '16px', backgroundColor: rc.color, border: '1px solid #ccc' }"
                ></span>
                <span class="font-weight-bold">{{ rc.label }}</span>
                <span class="text-muted">{{ rc.fromPosition }} - {{ rc.toPosition }}</span>
              </div>
              <button type="button" class="btn btn-sm btn-danger" @click="deleteRankingColor(idx)">
                <i class="fa fa-fw fa-trash"></i>
              </button>
            </div>
          </div>

          <hr v-if="model.rankingColors && model.rankingColors.length" class="my-3" />

          <div class="mt-3">
            <h5 class="h6 mb-2">{{ $t('rankingColor.create') }}</h5>
            <formly-form :form="rankingColorCreateForm"
                         :model="rankingColorCreateModel"
                         :fields="rankingColorCreateFields"></formly-form>
            <button type="button" class="btn btn-success" @click="createRankingColor()">{{ $t('save') }}</button>
          </div>
        </ntm-block>
      </form>
    </modal>
    <le-schedule-game-modal ref="scheduleModal" :venues="model.venues || []" @saved="onGameSaved"></le-schedule-game-modal>
    <modal name="final-four" :adaptive="true" height="auto">
      <ntm-block title="F4 LEADERS" :collapsable="false">
        <template #options>
          <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('final-four')" v-tooltip="$t('close')">
            <i class="fa fa-times"></i>
          </button>
        </template>
        <div class="mb-3">
          <div v-if="!finalFourPlayers.length">No player stats</div>
          <div v-for="player in finalFourPlayers" :key="player.name">{{player.name}} - {{player.value}}</div>
        </div>
      </ntm-block>
    </modal>
  </div>
</template>
<script>
import seasonShowMixin from '@/mixins/season.show.mixin'
import ScheduleService from '../../services/schedule.service'
import _ from 'lodash'
import TeamService from '../../services/team.service'
import generateScheduleFormDefinition from './formDefinitions/generateScheduleFormDefinition'
import addGameScheduleFormDefinition from './formDefinitions/addGameScheduleFormDefinition'
import GameService from '../../services/game.service'
import PdfGameDownload from '../../components/shared/PdfGameDownload'
import DocxGameDownload from '../../components/shared/DocxGameDownload'
import XlsxGameDownload from '../../components/shared/XlsxGameDownload'
import { gameStatusColor } from '@/lib/status'
import LeScheduleGameModal from '@/components/le/LeScheduleGameModal.vue'
import LeScheduleCalendarView from '@/components/le/LeScheduleCalendarView.vue'
import AddTeamForm from '@/views/schedules/createTeam/AddTeamForm.vue'
import updateScheduleFormDefinition from '@/views/schedules/formDefinitions/updateScheduleFormDefinition'
import TeamPlayersImagesDownload from '@/components/shared/TeamPlayersImagesDownload.vue'
import LeGamesTable from '@/components/le/LeGamesTable.vue'
import Multiselect from 'vue-multiselect'
import { useTimeline } from '@/use/useTimeline'
import { useGameSwap } from '@/use/useGameSwap'
import fantasyCreateFormDefinition from '@/views/schedules/formDefinitions/fantasyCreateFormDefinition'
import { formattedGameCode } from '@/lib/gameCode'
import LeagueRankingTable from '@/components/le/LeRankingTable.vue'
import WordLeagueScheduleDownload from '@/components/shared/WordLeagueScheduleDownload.vue'

export default {
  mixins: [seasonShowMixin],
  components: {
    LeScheduleGameModal,
    LeScheduleCalendarView,
    WordLeagueScheduleDownload,
    LeagueRankingTable,
    Multiselect,
    LeGamesTable,
    TeamPlayersImagesDownload,
    PdfGameDownload,
    DocxGameDownload,
    XlsxGameDownload,
    AddTeamForm
  },
  data () {
    return {
      viewMode: localStorage.getItem('schedule-view-mode') || 'list',
      finalFourPlayers: [],
      nameImagesLoading: false,
      addGameForm: {},
      addGameFields: addGameScheduleFormDefinition,
      addGameData: {
        round: 1,
        group: {},
        teamA: {},
        teamB: {},
        is_exhibition: false
      },
      editScheduleForm: {},
      deleteScheduleConfirmed: false,
      isInitialLoad: true,
      // Create ranking color form state
      rankingColorCreateForm: {},
      rankingColorCreateFields: [
        {
          key: 'label',
          type: 'input',
          templateOptions: { label: 'label' }
        },
        {
          key: 'color',
          type: 'input',
          templateOptions: { label: 'color', inputType: 'color' }
        },
        {
          key: 'fromPosition',
          type: 'input',
          templateOptions: { label: 'fromPosition', inputType: 'number' }
        },
        {
          key: 'toPosition',
          type: 'input',
          templateOptions: { label: 'toPosition', inputType: 'number' }
        }
      ],
      rankingColorCreateModel: { label: '', color: '#0088ff', fromPosition: null, toPosition: null },
      generateForm: {},
      generateFields: generateScheduleFormDefinition,
      generateData: {
        cycles: 1
      },
      fantasyCreateForm: {},
      fantasyCreateFields: fantasyCreateFormDefinition,
      fantasyCreateData: {},
      showColumns: [
        {
          name: 'name',
          prop: 'name'
        }
      ],
      model: {
        games: [],
        groups: [],
        medals: []
      },
      leagueGamesFilters: {
        isTerminated: false,
        status: [],
        filterByATeams: [],
        filterByBTeams: []
      }
    }
  },
  computed: {
    isAiNewsLocked () {
      return this.$store.getters.disabledFeatures.includes('ai-news')
    },
    filteredByATeamsUuid () {
      return this.leagueGamesFilters.filterByATeams.map((team) => team.uuid)
    },
    filteredByBTeamsUuid () {
      return this.leagueGamesFilters.filterByBTeams.map((team) => team.uuid)
    },
    filteredGames () {
      const games = {}

      Object.keys(this.model.games).forEach((round, r) => {
        games[round] = this.model.games[round].filter((game) => {
          if (this.leagueGamesFilters.isTerminated && !game.isTerminated) {
            return false
          }

          if (this.leagueGamesFilters.status.length && !this.leagueGamesFilters.status.includes(game.status)) {
            return false
          }

          if (this.filteredByATeamsUuid.length && !this.filteredByATeamsUuid.includes(game.teamA?.uuid) && !this.filteredByATeamsUuid.includes(game.teamB?.uuid)) {
            return false
          }

          if (this.filteredByBTeamsUuid.length && !this.filteredByBTeamsUuid.includes(game.teamA?.uuid) && !this.filteredByBTeamsUuid.includes(game.teamB?.uuid)) {
            return false
          }

          if (this.filteredByATeamsUuid.length && this.filteredByBTeamsUuid.length) {
            return (this.filteredByATeamsUuid.includes(game.teamA?.uuid) && this.filteredByBTeamsUuid.includes(game.teamB?.uuid)) ||
              (this.filteredByATeamsUuid.includes(game.teamB?.uuid) && this.filteredByBTeamsUuid.includes(game.teamA?.uuid))
          }

          return true
        })
      })

      return games
    },
    config () {
      return this.$store.state.config
    },
    settings () {
      return this.$store.state.settings
    },
    showRegenerateButton () {
      // Show generate/regenerate button if not all teams have the same games_count
      const teams = this.allTeams || []

      // Collect distinct games_count values
      const counts = new Set()
      for (const t of teams) {
        if (t && typeof t.games_count === 'number') {
          counts.add(t.games_count)
        } else {
          // If structure is unexpected, be conservative and hide button
          return false
        }
        if (counts.size > 1) {
          // As soon as we detect a difference, return true
          return true
        }
      }
      // All counts equal
      return false
    },
    allTeams () {
      const teams = []
      this.model.groups.forEach((group) => {
        teams.push(...group.teams)
      })

      return teams
    },
    editScheduleFields () {
      const dynamicFields = [...updateScheduleFormDefinition]

      if (this.config.ai) {
        if (!this.isAiNewsLocked && !dynamicFields.find(f => f.key === 'hasAIGamesNews')) {
          dynamicFields.push({
            key: 'hasAIGamesNews',
            type: 'boolean',
            templateOptions: {
              label: 'hasAIGamesNews'
            }
          })
        }

        if (!this.isAiNewsLocked && !dynamicFields.find(f => f.key === 'aiGamesNewsHighlight')) {
          dynamicFields.push({
            key: 'aiGamesNewsHighlight',
            type: 'ntm-select',
            isString: true,
            options: [
              'ALL_GAMES',
              'CLOSE_AND_IMPORTANT_GAMES',
              'NONE_GAMES'
            ],
            templateOptions: {
              label: 'aiGamesNewsHighlight',
              translated: true
            }
          })
        }

        if (!this.isAiNewsLocked && !dynamicFields.find(f => f.key === 'aiLeaguePreviousWeekSummaryNews')) {
          dynamicFields.push({
            key: 'aiLeaguePreviousWeekSummaryNews',
            type: 'ntm-select',
            isString: true,
            options: ['OFF', 'EVERY_MONDAY', 'EVERY_TUESDAY', 'EVERY_WEDNESDAY', 'EVERY_THURSDAY', 'EVERY_FRIDAY', 'EVERY_SATURDAY', 'EVERY_SUNDAY'],
            templateOptions: { translated: true, label: 'aiLeaguePreviousWeekSummaryNews' }
          })
        }

        // if (!dynamicFields.find(f => f.key === 'aiPlayerRecordsNews')) {
        //   dynamicFields.push({
        //     key: 'aiPlayerRecordsNews',
        //     type: 'boolean',
        //     templateOptions: { label: 'aiPlayerRecordsNews' }
        //   })
        // }
      }

      const extendsSchedulesField = dynamicFields.find(f => f.key === 'extendsSchedules')
      if (extendsSchedulesField) {
        extendsSchedulesField.options = this.model.scheduleOptions?.filter(s => s.uuid !== this.model.uuid)
      }

      return dynamicFields.map(field => {
        if (field.key === 'show_player_in_game_time_on_website' || field.key === 'show_player_plus_minus_on_website') {
          return {
            ...field,
            templateOptions: {
              ...field.templateOptions,
              disabled: !this.model.has_substitutions
            }
          }
        }
        if (['aiLeaguePreviousWeekSummaryNews', 'aiPlayerRecordsNews'].includes(field.key)) {
          return field
        }
        return field
      })
    }
  },
  beforeRouteEnter (to, from, next) {
    ScheduleService.show(to.params.uuid).then((response) => {
      next(vm => vm.setData(response.data))
    })
  },
  setup () {
    const { fetchCurrentTimeline } = useTimeline()
    const { swapGame, swapGameTimeAndVenue } = useGameSwap()

    return {
      fetchCurrentTimeline, swapGame, swapGameTimeAndVenue
    }
  },
  created () {
    const queryView = this.$route.query.view
    if (queryView === 'list' || queryView === 'calendar') {
      this.viewMode = queryView
    } else {
      this.$router.replace({ query: { ...this.$route.query, view: this.viewMode } }).catch(() => null)
    }
  },
  mounted () {
    this.generateData.cycles = this.model.cycles ?? 2
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    openAddGameModal () {
      this.addGameData.is_exhibition = this.model.is_exhibition
      this.$refs.addGame.open()
    },
    handleKeydown (e) {
      if (e.altKey && e.code === 'KeyN') {
        e.preventDefault()
        this.openAddGameModal()
      }
    },
    openFinalFourLeaders () {
      ScheduleService.getFinalFourLeaders(this.$route.params.uuid).then((response) => {
        this.finalFourPlayers = response.data
        this.$modal.show('final-four')
      })
    },
    formattedGameCode (code) {
      return formattedGameCode(code)
    },
    upsertFantasy () {
      ScheduleService.upsertFantasy(this.$route.params.uuid, this.fantasyCreateData).then(() => {
        this.$refs.fantasyCreate.close()
        this.fetchData()
      })
    },
    deleteFantasy () {
      ScheduleService.deleteFantasy(this.$route.params.uuid, this.fantasyCreateData).then(() => {
        this.$refs.fantasyCreate.close()
        this.fetchData()
      })
    },
    async swapGameTimeVenueBetweenGames (game) {
      const swapGame = { ...game, league: { name: this.model.name } }
      await this.swapGameTimeAndVenue(swapGame)
      this.fetchData()
    },
    formatDate (time) {
      if (!time) {
        return ''
      }

      return this.$gameTime(time, 'numericDateTime')
    },
    toggleLeagueFilterStatus (status) {
      if (this.leagueGamesFilters.status.includes(status)) {
        this.$mixpanel.track('Schedule games filter', { type: 'STATUS_CHANGE', added: status })
        this.leagueGamesFilters.status = this.leagueGamesFilters.status.filter(arrayItem => arrayItem !== status)
      } else {
        this.$mixpanel.track('Schedule games filter', { type: 'STATUS_CHANGE', removed: status })
        this.leagueGamesFilters.status.push(status)
      }
    },
    groupByGameRound (playoffRound) {
      return playoffRound.games.reduce((group, game) => {
        const { round } = game
        group[round] = group[round] ?? []
        group[round].push(game)
        return group
      }, {})
    },
    updateSchedule () {
      ScheduleService.update(this.$route.params.uuid, this.model).then(() => {
        this.fetchData()
      }).finally(() => {
        this.$refs.editSchedule.close()
        this.$store.commit('loaded')
      })
    },
    getMedalClass (place) {
      switch (place) {
        case 1:
          return 'text-gold'
        case 2:
          return 'text-silver'
        case 3:
          return 'text-bronze'
      }
    },
    getNameOfRound (round) {
      switch (round) {
        case 8:
          return this.$t('playoff.round.8')
        case 4:
          return this.$t('playoff.round.4')
        case 2:
          return this.$t('playoff.round.2')
        case 1:
          return this.$t('playoff.round.1')
        default:
          return this.$t('playoff.round.bestOf', { round: round })
      }
    },

    setData (data) {
      this.model = data
      this.updateAddGameTeams()

      if (!this.model.fantasyLeague) {
        this.fantasyCreateData = {
          weeklyGamePredictionLimit: this.config.fantasy.weeklyGamePredictionLimit,
          teamPredictionScoreMultiplier: this.config.fantasy.teamPredictionScoreMultiplier,
          teamPredictionScoreMinValue: this.config.fantasy.teamPredictionScoreMinValue,
          starterPlayersCount: this.config.fantasy.roster.starterPlayersCount,
          benchPlayersCount: this.config.fantasy.roster.benchPlayersCount,
          minPlayerValue: this.config.fantasy.roster.minPlayerValue
        }
      } else {
        this.fantasyCreateData = this.model.fantasyLeague
      }
      this.$nextTick(() => {
        this.isInitialLoad = false
      })
    },
    fetchData () {
      ScheduleService.show(this.$route.params.uuid).then((response) => {
        this.setData(response.data)
      })
    },
    addGroup () {
      ScheduleService.addGroup(this.$route.params.uuid).then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      })
    },
    deleteGroup (uuid) {
      ScheduleService.deleteGroup(this.$route.params.uuid, uuid).then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      })
    },
    dragTeam (groupUuid, teamUuid) {
      ScheduleService.changeGroup(this.$route.params.uuid,
        teamUuid,
        { group: { uuid: groupUuid } }).then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      })
    },
    deleteTeam (uuid) {
      TeamService.destroy(this.$route.params.uuid, uuid).then(() => {
        this.fetchData()
      })
    },
    orderedTeams: function (teams) {
      return _.orderBy(teams, 'name')
    },
    teamName (team) {
      if (!team) {
        return 'TBD'
      }

      if (team?.name) {
        return team.name
      }

      return this.$t('emptySlot')
    },
    generate () {
      ScheduleService.generate(this.$route.params.uuid, this.generateData).then(() => {
        this.$refs.generateSchedule.close()
        this.fetchData()
        this.$store.commit('loaded')
      }).catch(() => {
        this.$refs.generateSchedule.close()
      })
    },
    regenerate () {
      ScheduleService.regenerate(this.$route.params.uuid, this.generateData).then(() => {
        this.$refs.regenerateSchedule.close()
        this.fetchData()
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
      }).finally(() => {
        this.$store.commit('loaded')
      })
    },
    addGame () {
      ScheduleService.addGame(this.$route.params.uuid, this.addGameData).then(() => {
        this.$refs.addGame.close()
        this.fetchData()
        this.addGameData.teamA = {}
        this.addGameData.teamB = {}
        this.$store.commit('loaded')
        this.addGameForm.$errors = {}
      }).catch((err) => {
        this.addGameForm.$errors = err.response.data.errors
        this.$store.commit('loaded')
      })
    },
    deleteGame (uuid) {
      GameService.destroy(this.$route.params.uuid, uuid).then(() => {
        this.fetchData()
      })
    },
    deleteAllGames () {
      return ScheduleService.deleteAllGames(this.$route.params.uuid).then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      })
    },
    deleteAllFriendlyGames () {
      return ScheduleService.deleteAllGames(this.$route.params.uuid, 'FRIENDLY').then(() => {
        this.fetchData()
        this.$store.commit('loaded')
      })
    },
    gameStatusColor,
    updateAddGameTeams () {
      this.addGameData.teamA = {}
      this.addGameData.teamB = {}
      this.addGameFields.find(f => f.key === 'teamA').options = this.allTeams
      this.addGameFields.find(f => f.key === 'teamB').options = this.allTeams
    },
    // Ensure input color is stored as 7-char HEX (#RRGGBB)
    normalizeToHexColor (value) {
      if (!value) return value
      const v = String(value).trim()
      // If already in hex form
      if (/^#([0-9a-f]{3})$/i.test(v)) {
        // expand #abc to #aabbcc
        const r = v[1]; const g = v[2]; const b = v[3]
        return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
      }
      if (/^#([0-9a-f]{6})$/i.test(v)) {
        return `#${v.slice(1).toLowerCase()}`
      }
      // rgb or rgba
      const m = v.match(/^rgba?\((\s*\d+\s*),(\s*\d+\s*),(\s*\d+\s*)(?:,\s*([\d.]+)\s*)?\)$/i)
      if (m) {
        const toHex = (n) => {
          const x = Math.max(0, Math.min(255, parseInt(n, 10) || 0))
          return x.toString(16).padStart(2, '0')
        }
        return `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`.toLowerCase()
      }
      // Named colors or other formats - try using browser to compute
      try {
        const el = document.createElement('div')
        el.style.color = v
        document.body.appendChild(el)
        const cs = getComputedStyle(el).color
        document.body.removeChild(el)
        const mm = cs.match(/^rgba?\((\s*\d+\s*),(\s*\d+\s*),(\s*\d+\s*)/i)
        if (mm) {
          const toHex = (n) => {
            const x = Math.max(0, Math.min(255, parseInt(n, 10) || 0))
            return x.toString(16).padStart(2, '0')
          }
          return `#${toHex(mm[1])}${toHex(mm[2])}${toHex(mm[3])}`.toLowerCase()
        }
      } catch (e) {
        // ignore
      }
      return v
    },
    createRankingColor () {
      const payload = { ...this.rankingColorCreateModel }
      // Basic validation
      if (!payload.label || !payload.color || payload.fromPosition == null || payload.toPosition == null) {
        this.rankingColorCreateForm.$errors = { _self: ['Please fill in all fields'] }
        return
      }
      // Force HEX color storage
      payload.color = this.normalizeToHexColor(payload.color)
      ScheduleService.createRankingColor(this.$route.params.uuid, payload)
        .then((res) => {
          this.model.rankingColors.push({ ...payload, uuid: res.data.uuid })
          // Reset form
          this.rankingColorCreateModel = { label: '', color: '#0088ff', fromPosition: null, toPosition: null }
          if (this.rankingColorCreateForm) this.rankingColorCreateForm.$errors = {}
        })
    },
    deleteRankingColor (index) {
      const uuid = this.model.rankingColors[index]?.uuid
      if (!uuid) return
      if (!Array.isArray(this.model.rankingColors)) return
      const nextColors = this.model.rankingColors.slice()
      nextColors.splice(index, 1)
      // Persist only the changed rankingColors to minimize payload; backend should merge/update
      ScheduleService.deleteRankingColor(this.$route.params.uuid, uuid)
        .then(() => {
          this.model.rankingColors = nextColors
        })
    },
    onCalendarGameClick (game) {
      if (['NOT_PLAYED', 'SCHEDULED'].includes(game.status)) {
        this.$refs.scheduleModal.open(game, this.$route.params.uuid)
      }
    },
    scheduleGameModal (game) {
      this.$refs.scheduleModal.open(game, this.$route.params.uuid)
    },
    onGameSaved () {
      this.$mixpanel.track('Schedule game on schedule page')
    },
    cancelScheduledGame (game) {
      GameService.cancelScheduledGame(this.$route.params.uuid, game.uuid).then((res) => {
        game.time = res.data.time
        game.venue = res.data.venue
        game.code = res.data.code
        game.status = res.data.status

        this.fetchCurrentTimeline()
      }).finally(() => {
        this.$mixpanel.track('Cancel scheduled game on schedule page')
      })
    },
    deleteSchedule (id) {
      ScheduleService.delete(id).then(() => {
        this.$router.push({ name: 'schedules.index' })
      })
    },
    forceUpdate () {
      this.$forceUpdate()
    }
  },
  watch: {
    viewMode (val) {
      localStorage.setItem('schedule-view-mode', val)
      this.$router.replace({ query: { ...this.$route.query, view: val } }).catch(() => null)
    },
    'model.has_substitutions' (val) {
      if (this.isInitialLoad) return

      if (val) {
        this.model.show_player_in_game_time_on_website = true
        this.model.show_player_plus_minus_on_website = true
      } else {
        this.model.show_player_in_game_time_on_website = false
        this.model.show_player_plus_minus_on_website = false
      }
    },
    'leagueGamesFilters.filterByATeams': {
      handler: function () {
        this.$mixpanel.track('Schedule games filter', { type: 'TEAM_CHANGE' })
      },
      deep: true
    },
    'leagueGamesFilters.filterByBTeams': {
      handler: function () {
        this.$mixpanel.track('Schedule games filter', { type: 'TEAM_CHANGE' })
      },
      deep: true
    }
  }
}
</script>

<style type="scss">
.short-block {
  max-width: 800px;
  margin: 0 auto 20px auto;
}

.game-round {
  margin-bottom: 15px;
}

.groups .block-content .row {
  min-height: 50px;
  padding-bottom: 30px;
}

.groups .team-wrapper {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-top: 5px;
  padding-bottom: 5px;
  border-bottom: 1px solid #eeeeee;
}

.groups .team span {
  //line-height: 30px;
}

.groups .team .btn {
  float: right;
  margin-left: 5px;
}

.team-right-side {
  display: flex;
}

.team-right-side-item {
  display: flex;
  align-items: center;
}
</style>
